/*
 * Closure-free helpers at module scope (javascript:S7721), prefixed to avoid colliding with other
 * files in the same bundle — same shape and same reasoning as pshReportHighlightProblem in
 * payer_survey_helper.js.
 *
 * Sentry is a bare global from lib/thirdparty/sentry, and not every portal entry point loads it,
 * which is why portalHelper's retry reporter and payerSurveyHelper's highlight reporter both
 * `typeof`-guard it before use. Referenced bare, an absent Sentry throws a ReferenceError, and
 * every caller of these two is either a catch block or a timer callback — so that throw would
 * escape into nothing, leaving the export button flagged as processing with no toast and no
 * telemetry. Reporting a failure must never become one.
 *
 * console first, as portal_helper.js and payer_survey_helper.js both do: show_export_toast() funnels
 * its own swallowed failure through here, so when Sentry is absent too this is the only thing
 * standing between a failed export and total silence.
 */
function dtuCaptureExportException(error, context) {
  console.error('server-side export failed', error);
  if (typeof Sentry !== 'undefined' && typeof Sentry.captureException === 'function') {
    Sentry.captureException(error, context);
  }
}

/*
 * Defaults the level to warning, matching portal_helper.js and payer_survey_helper.js. Every message
 * sent from here is an export that failed where the clicking user may be the only witness, and
 * Sentry's default of `info` is the easiest level to filter out of issue views and alerting — these
 * are the only signal such a failure produces, so they must not arrive as noise.
 */
function dtuCaptureExportMessage(message, context) {
  console.warn('server-side export: ' + message);
  if (typeof Sentry !== 'undefined' && typeof Sentry.captureMessage === 'function') {
    Sentry.captureMessage(message, angular.extend({ level: 'warning' }, context));
  }
}

app.service('dataTablesUtils', ['$filter', 'CONSTANTS', '$injector', function ($filter, CONSTANTS, $injector) {
  const dataTablesUtils = this;

  // RPR-6314: read page/search/sort off a live table before a bDestroy rebuild throws it away.
  // Returns the defaults a brand-new DataTable would use when there is no outgoing table, so the
  // caller can feed the result straight back through the constructor either way.
  dataTablesUtils.captureTableState = function (tableSelector) {
    if (!$.fn.dataTable.isDataTable(tableSelector)) {
      return {order: [], search: '', start: 0};
    }
    // Re-read rather than trust a cached handle, which can point at an already-destroyed instance.
    const table = $(tableSelector).DataTable();
    return {order: table.order(), search: table.search(), start: table.page.info().start};
  };

  // RPR-6314: DataTables' constructor clamps an out-of-range displayStart to page 1 - the very
  // symptom RPR-6314 fixes - rather than to the last page the way its runtime paging does. Returns
  // the page to land on, or null when there is nothing to correct.
  //
  // previousStart is always a page boundary, so a user genuinely on page 1 gives 0 and is skipped.
  // pages === 0 (the restored search now matches nothing) also returns null: there is no page to
  // land on, and DataTables' own "filtered from N total entries" line already explains the empty
  // table. NOTE the "start === 0 means the clamp fired" inference holds only while stateSave is
  // off - DataTables' state-load path writes displayStart too, which would make start === 0 mean
  // something else entirely. Verified against the vendored DataTables 2.3.8; re-check on a bump.
  dataTablesUtils.lastValidPage = function (pageInfo, previousStart) {
    if (!pageInfo || previousStart <= 0 || pageInfo.start !== 0 || pageInfo.pages <= 1) {
      return null;
    }
    return pageInfo.pages - 1;
  };

  dataTablesUtils.getColIndex = function (table_id, data_name) {
    const t = $('#' + table_id).DataTable();

    function match_column(o) {
      return o.data === data_name;
    }

    return t.settings().init().columns.findIndex(match_column);
  };

  dataTablesUtils.applyColFilter = function (table_id, data_name, filter_expression) {
    const col_index = dataTablesUtils.getColIndex(table_id, data_name);
    $('#' + table_id).DataTable().columns(col_index).search(filter_expression, true, false).draw();
  };

  dataTablesUtils.is_export_column = function (table, idx) {
    const is_visible = (table.column(idx).visible());
    if (is_visible) {
      // do not export skipCol (such as Options button)
      if (table.column(idx).header().className.indexOf('skipCol') !== -1) {
        return false;
      }
    }
    return is_visible;
  };

  dataTablesUtils.get_export_columns = function (table, columns) {
    if (!angular.isDefined(table)) {
      alert('report_columns is not defined');
      return;
    }

    // returns visible columns except skipCol
    const export_columns = $.grep(columns, function (v, i) {
      return dataTablesUtils.is_export_column(table, i);
    });
    return export_columns;
  };

  const getPageSizeForPDF = function (numberOfColumns) {
    const pageSizes = [
      { from: 0,  to: 12, size: 'A4' },
      { from: 12, to: 20, size: 'A3' },
      { from: 20, to: 28, size: 'A2' },
      { from: 28, to: 38, size: 'A1' },
      { from: 38, to: 50, size: 'A0' },
      { from: 50, to: 62, size: '2A0' },
      { from: 62, to: 74, size: '4A0' }
    ];

    const pageSize = _.find(pageSizes, function (size) {
      return (numberOfColumns >= size.from && numberOfColumns < size.to);
    });

    if (pageSize !== undefined) {
      return pageSize.size;
    }

    return 'A4';
  };

  dataTablesUtils.get_pdf_button = function (report_name, user, scope, dropdown, custom_headers, report_table_key, report_columns_key) {
    const btn_pdf = {
      extend: 'pdfHtml5',
      title: report_name,
      exportOptions: {
        orthogonal: 'export',
        columns: function (idx, data, node) {
          return dataTablesUtils.is_export_column(scope[report_table_key], idx);
        },
        format: {
          footer: function ( data, row, column, node ) {
            // Strip <br/> with newline
            return data.replace( /<br\s*[/]?>/gi, ' / ');
          }
        }
      },
      orientation: 'landscape',
      text: 'Export PDF',
      footer: true,
      className: (dropdown) ? 'mat ui export-pdf-dropdown primary button' : 'mat ui export-pdf-button small primary button',
      customize: function (doc) {
        doc.styles.tableHeader.fontSize = 10;
        doc.defaultStyle.fontSize = 9;
        doc.styles.title.alignment = 'center';
        doc.pageMargins = [40, 60, 40, 40];
        let content_index = 1;

        // Splice the text in after the header, but before the table
        doc.content.splice(1, 0, {
          margin: [0, 0, 0, 12],
          alignment: 'left',
          text: 'Date: ' + $filter('date')(
            new Date(),
            CONSTANTS.dateTimeFormat.MEDIUM_24H_TIME_NO_SEC)
        });
        content_index += 1;

        // insert custom_headers
        if (custom_headers && (typeof custom_headers === 'string')) {
          doc.content.splice(content_index, 0, {
            margin: [0, 0, 0, 12],
            alignment: 'left',
            text: custom_headers
          });
          content_index += 1;
        } else if (custom_headers && custom_headers.length) {
          for (let i = 0; i < custom_headers.length; i++) {
            doc.content.splice(content_index, 0, {
              margin: [0, 0, 0, 12],
              alignment: 'left',
              text: custom_headers[i]
            });
            content_index += 1;
          }
        }

        if (user) {
          doc.content.splice(1, 0, {
            alignment: 'left',
            text: 'User: ' + user.first_name + ' ' + user.last_name
          });
          content_index += 1;
        }

        // make number columns right aligned
        const visible_columns = dataTablesUtils.get_export_columns(scope[report_table_key],
          scope[report_columns_key]);
        doc['pageSize'] = getPageSizeForPDF(visible_columns.length);

        const rowCount = doc.content[content_index].table.body.length;

        // iterate over rows
        for (let i = 1; i < rowCount; i++) {
          // iterate over visible columns and apply alignment to pdf
          for (let col_index = 0; col_index < visible_columns.length; col_index++) {
            if (visible_columns[col_index].type == 'num') {
              doc.content[content_index].table.body[i][col_index].alignment = 'right';
            }
          }
        }

        // iterate over table headers
        for (let col_index = 0; col_index < visible_columns.length; col_index++) {
          const alignment = visible_columns[col_index].type == 'num' ? 'right' : 'left';
          if (doc.content[content_index].table.body[0][col_index] != null) {
            doc.content[content_index].table.body[0][col_index].alignment = alignment;
          }
        }

        doc['header'] = (function (page, pages) {
          return {
            columns: [
              //'eee',
              {
                alignment: 'left',
                margin: [40, 20, 0, 0],
                // image: base64 of resized RPR_stacked_final.png
                image: 'data:image/png;base64,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',
                height: 30,
                width: 93
              }
            ]
          };
        });

        doc['footer'] = (function (page, pages) {
          return {
            columns: [
              {
                alignment: 'center',
                text: [
                  { text: page.toString() },
                  ' of ',
                  { text: pages.toString() }
                ]
              }
            ],
            margin: [0, 10, 0, 0]
          };
        });
      }
    };

    return btn_pdf;
  };

  dataTablesUtils.get_csv_button = function (report_name, user, scope, dropdown, custom_headers, report_table_key, report_columns_key) {
    const btn_csv = {
      extend: 'csvHtml5',
      bom: true,
      text: 'Export CSV',
      className: (dropdown) ? 'mat ui export-csv-dropdown primary button' : 'mat ui export-csv-button small primary button',
      title: report_name,
      footer: true,
      exportOptions: {
        format: {
          footer: function ( data, row, column, node ) {
            // Strip <br/> with newline
            return data.replace( /<br\s*[/]?>/gi, ' / ');
          }
        },
        orthogonal: 'export',
        columns: function (idx, data, node) {
          return dataTablesUtils.is_export_column(scope[report_table_key], idx);
        }
      },
      customize: function (csv, config) {
        const visible_columns = dataTablesUtils.get_export_columns(scope[report_table_key],
          scope[report_columns_key]).length;

        function GetRow(csv, column_count, row_text) {
          return ['"' + row_text + '"'].concat(Array(column_count - 1)).join(',') + '\n';
        }

        function wrapValue(value) {
          if (value.trim() !== '' && value.startsWith('"0')) {
            // If values starts with zero then add tab to prevent leading zero error in excel.
            // replace defaul csv single value from "\"01234561 \"" to "'01234561'"
            return value.replace(/^"(.*)"$/, '\t$1');
          }
          return value;
        }

        const r1 = GetRow(csv, visible_columns, report_name);
        const r2 = (user)
          ? GetRow(csv, visible_columns, 'User: ' + user.first_name + ' ' + user.last_name)
          : '';

        const r3 = GetRow(csv,
          visible_columns,
          'Date: ' + $filter('date')(new Date(), CONSTANTS.dateTimeFormat.MEDIUM_24H_TIME_NO_SEC));

        const r0 = GetRow(csv, visible_columns, '');
        let r_custom_headers = '';

        if (custom_headers && (typeof custom_headers === 'string')) {
          r_custom_headers = GetRow(csv, visible_columns, custom_headers);
        } else if (custom_headers && custom_headers.length) {
          for (let i = 0; i < custom_headers.length; i++) {
            r_custom_headers += GetRow(csv, visible_columns, custom_headers[i]);
          }
        }

        const rows = csv.split('\n');
        let csvContent = rows[0].trim() + '\n';
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i].trim();
          if (row) {
            const values = row.split(',');
            csvContent += values.map(wrapValue).join(',') + '\n';
          }
        }
        return r1 + r0 + r2 + r3 + r_custom_headers + r0 + csvContent;
      }
    };

    return btn_csv;
  };

  dataTablesUtils.excelInsertRow = function (sheet, insert_row_index, row_text) {
    const key = 'A';
    const clRow = $('row', sheet);

    //update Row
    clRow.each(function () {
      const attr = $(this).attr('r');
      let ind = parseInt(attr);

      if (ind < insert_row_index) {
        return;
      }

      ind = ind + 1;
      $(this).attr('r', ind);
    });

    // Update  row > c
    $('row c ', sheet).each(function () {
      const attr = $(this).attr('r');
      const pre = attr.substring(0, 1);
      let ind = parseInt(attr.substring(1, attr.length));

      if (ind < insert_row_index) {
        return;
      }

      ind = ind + 1;
      $(this).attr('r', pre + ind);
    });

    const new_row = ['<row r="' + insert_row_index + '">',
      '<c t="inlineStr" r="' + key + insert_row_index + '" s="0">',
      '<is>',
      '<t>' + row_text + '</t>',
      '</is>',
      '</c>',
      '</row>'].join('\n');

    sheet.childNodes[0].childNodes[1].innerHTML = new_row + sheet.childNodes[0].childNodes[1].innerHTML;
  };

  dataTablesUtils.get_excel_button = function (report_name, user, scope, dropdown, custom_headers, report_table_key, report_columns_key, custom_styles) {
    const btn_excel = {
      extend: 'excelHtml5',
      autoFilter: true,
      createEmptyCells: true,
      title: '',
      text: 'Export Excel',
      className: (dropdown) ? 'mat ui export-excel-dropdown primary button' : 'mat ui export-excel-button small primary button',
      filename: report_name,
      footer: true,
      exportOptions: {
        format: {
          footer: function ( data, row, column, node ) {
            // Strip <br/> with newline
            return data.replace( /<br\s*[/]?>/gi, ' / ');
          }
        },
        columns: function (idx, data, node) {
          return dataTablesUtils.is_export_column(scope[report_table_key], idx);
        },
        orthogonal: 'export',
        stripNewlines: false
      },
      customize: function (xlsx, button_config, table_api) {
        const sheet = xlsx.xl.worksheets['sheet1.xml'];

        const styles = xlsx.xl['styles.xml'];
        const lastXfIndex = $('cellXfs xf', styles).length - 1;

        // Generate custom numfmt for date
        // Id and values are what Excel generates for the format we require
        let customNumFmt = '<numFmt numFmtId="170" formatCode="[$-409]d mmm yyyy;@"/>';
        styles.childNodes[0].childNodes[0].innerHTML += customNumFmt;

        // Generate custom numfmt for date+time
        customNumFmt = '<numFmt numFmtId="175" formatCode="dd mmm yyyy h:mm &quot;GMT&quot;;@"/>';
        styles.childNodes[0].childNodes[0].innerHTML += customNumFmt;

        // General - Right, Default bordered style ID for General Text
        // Creating general right aligned style
        const generalRightAlignedStyleId = lastXfIndex + 1;
        let customStyle = '<xf numFmtId="0" fontId="0" applyFont="1" borderId="1" applyBorder="1" applyFill="0" xfId="0" applyAlignment="1">' +
            '<alignment horizontal="right" vertical="top"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // Header Right - Create custom style for Right Aligned Headings
        // Bold, grey background, thin black border, right aligned
        const headerRightAlignedStyleId = lastXfIndex + 2;
        customStyle = '<xf numFmtId="0" fontId="2" applyFont="1" borderId="1" applyBorder="1" fillId="2" applyFill="1" xfId="0" applyAlignment="1">' +
            '<alignment horizontal="right"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // General Left - Normal, thin black border, top aligned
        const generalLeftAlignedStyleId = lastXfIndex + 3;
        customStyle = '<xf numFmtId="0" borderId="1" applyBorder="1" xfId="0" applyAlignment="1">' +
            '<alignment vertical="top"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // Header Left - Create custom style for Left Aligned Headings (mostly at index 70)
        // Bold, grey background, thin black border, left aligned
        const headerLeftAlignedStyleId = lastXfIndex + 4;
        customStyle = '<xf numFmtId="0" fontId="2" applyFont="1" borderId="1" applyBorder="1" fillId="2" applyFill="1" xfId="0" applyAlignment="1">' +
            '<alignment horizontal="left"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // General Date - Custom format for date
        const dateRightAlignedStyleId = lastXfIndex + 5;
        customStyle = '<xf numFmtId="170" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1" applyAlignment="1">' +
            '<alignment horizontal="right" vertical="top"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // General Date - Custom format for date
        const dateTimeRightAlignedStyleId = lastXfIndex + 6;
        customStyle = '<xf numFmtId="175" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1" applyAlignment="1">' +
            '<alignment horizontal="right" vertical="top"/></xf>';
        styles.childNodes[0].childNodes[5].innerHTML += customStyle;

        // append custom style ruels to the existing styles
        if (custom_styles && custom_styles.appendStyleDefinations) {
          custom_styles.appendStyleDefinations(styles);
        }

        const defaultRowHeight = 11;

        const visible_columns = dataTablesUtils.get_export_columns(scope[report_table_key],
          scope[report_columns_key]);
        let headerRows = 0;

        if (custom_headers && (typeof custom_headers === 'string')) {
          dataTablesUtils.excelInsertRow(sheet, 1, custom_headers);
          headerRows += 1;
        } else if (custom_headers && custom_headers.length) {
          for (let i = custom_headers.length - 1; i >= 0; i--) {
            dataTablesUtils.excelInsertRow(sheet, 1, custom_headers[i]);
            headerRows += 1;
          }
        }

        dataTablesUtils.excelInsertRow(sheet,
          1,
          'Date: ' + $filter('date')(new Date(), CONSTANTS.dateTimeFormat.MEDIUM_24H_TIME_NO_SEC));
        headerRows += 1;

        if (user) {
          dataTablesUtils.excelInsertRow(sheet, 1, 'User: ' + user.first_name + ' ' + user.last_name);
          headerRows += 1;
        }
        dataTablesUtils.excelInsertRow(sheet, 1, report_name);
        headerRows += 1;

        const totalDataRows = $('row:gt(' + headerRows + ')', sheet).length;

        const tableData = [];
        table_api.rows({ search: 'applied' }).every(function () {
          tableData.push(this.data());
        });

        // make number columns right aligned
        // iterate over rows
        $('row:gt(' + headerRows + ')', sheet).each(function (row_index, value) {
          // iterate over visible columns and apply alignment to excel

          // get className for row if custom_styles are defined
          let className = null;
          if (custom_styles && row_index != totalDataRows - 1) { // skip the summary column
            if (custom_styles.getClassName) {
              className = custom_styles.getClassName(tableData[row_index], null);
            }
          }

          for (let col_index = 0; col_index < visible_columns.length; col_index++) {

            // ignore footer cells
            if (row_index === totalDataRows - 1 && $('c', this).eq(col_index).attr('s') == '2') {
              return;
            }

            if (className) {
              $('c', this).eq(col_index).attr('s', custom_styles.ids[className]);
            } else {
              $('c', this).eq(col_index).attr('s', generalRightAlignedStyleId.toString());
              if (visible_columns[col_index].type === 'num') {
                // Normal text, thin black border, right align
                $('c', this).eq(col_index).attr('s', generalRightAlignedStyleId.toString());
              } else if (visible_columns[col_index].type === 'date') {
                const currentCell = $('c', this).eq(col_index);
                const colDef = visible_columns[col_index];
                const rowData = tableData[row_index] || {};
                const rawValue = rowData ? rowData[colDef.data] : '';
                const dateValue = (rawValue == null) ? '' : String(rawValue);
                const hasTime = dateValue.indexOf(':') >= 0;

                if (dateValue) {
                  currentCell.removeAttr('t');
                  let m = hasTime ? moment.utc(dateValue) : moment.utc(dateValue, ['YYYY-MM-DD', moment.ISO_8601], true);
                  if (!m.isValid()) {
                    m = moment.utc(dateValue);
                  }
                  if (m.isValid()) {
                    const excelDateInt = (m.unix() / 86400) + 25569;
                    if (hasTime) {
                      currentCell[0].innerHTML = '<v>' + excelDateInt.toString() + '</v>';
                      currentCell.attr('s', dateTimeRightAlignedStyleId.toString());
                    } else {
                      currentCell[0].innerHTML = '<v>' + Math.trunc(excelDateInt).toString() + '</v>';
                      currentCell.attr('s', dateRightAlignedStyleId.toString());
                    }
                  } else {
                    currentCell[0].innerHTML = '<v></v>';
                  }
                } else {
                  currentCell[0].innerHTML = '<v></v>';
                }
              } else {
                // Normal text, thin black border, top
                $('c', this).eq(col_index).attr('s', generalLeftAlignedStyleId.toString());
              }
            }
          }
        });

        // iterate over table headers
        $('row:eq(' + headerRows + ')', sheet).each(function () {
          // iterate over visible columns and apply alignment to excel
          for (let col_index = 0; col_index < visible_columns.length; col_index++) {
            if (visible_columns[col_index].type === 'num' || visible_columns[col_index].type === 'date') {
              // Bold, grey background, thin black border, right aligned
              $('c', this).eq(col_index).attr('s', headerRightAlignedStyleId.toString());
            } else {
              // Bold, grey background, thin black border, left aligned
              $('c', this).eq(col_index).attr('s', headerLeftAlignedStyleId.toString());
            }
          }
        });

        /*
           * Fixing the auto filter value in Excel file as we have ended up adding additional
           * headers to the file and we need to recalculate where the headers are to place the
           * auto filter value
           */

        // We always start the report from the A cell. Depending on Header Rows, we will move it down.
        const startCell = 'A' + (headerRows + 1).toString();

        /*
            * Calculating ending cell depending on columns & rows
            * We need to take into consideration column lengths over 26 (i.e. 26 chars of the alphabet)
            * and into the territory of 'AA', 'AB', etc.
            *
            * Assumption:
            * Our assumption is that we would have a max of ZZ columns and not beyond that.
            * i.e. 26 x 26 = 676
           */
        const charsInAlphabet = 26;
        let firstChar = Math.floor(visible_columns.length / charsInAlphabet);
        let secondChar = visible_columns.length % charsInAlphabet;
        const asciiA = 'A'.charCodeAt();

        // Cleaning up for edge cases (eg: Z column, AZ column, etc)
        if (secondChar === 0 && firstChar > 0) {
          firstChar --;
          secondChar = charsInAlphabet;
        }

        let endChar = '';
        if (firstChar > 0) {
          endChar = String.fromCharCode(asciiA + firstChar - 1);
        }
        if (secondChar > 0) {
          endChar += String.fromCharCode(asciiA + secondChar - 1);
        }

        const endCell = endChar + (totalDataRows + headerRows + 1).toString();
        sheet.childNodes[0].children[2].attributes['ref'].value = startCell + ':' + endCell;
      }
    };

    return btn_excel;
  };

  /*
   * Ceiling on the full-table fetch below. If neither draw.dt nor xhr.dt ever arrives, tear the
   * export down rather than leave the handlers armed: the user is told and the button released
   * instead of watching a spinner indefinitely, and a much later unrelated interaction cannot pop
   * this export's own 'the list changed' error over whatever they are doing by then. (It is no
   * longer what stops a rogue download — the draw check does that.) Two ways to get here: a request
   * that simply never completes, or
   * a 200 response carrying a non-JSON body (proxy / auth interstitial) — jQuery only runs
   * dataFilter on success, and the caller-supplied dataTableAjaxSafeDataFilter (app_common.js)
   * then throws straight out of jQuery before it resolves or rejects, so neither callback runs.
   * A 5xx error page is NOT this case: that rejects normally and reaches handlers.xhr.
   */
  const SERVER_SIDE_EXPORT_TIMEOUT_MS = 120000;

  /*
   * Second ceiling, armed once the rows are in and the exporter has taken over. excelHtml5 hands
   * the zipping to JSZip's generateAsync(...).then(release) with no .catch, so a rejection there
   * (or a browser refusing the download) would otherwise leave the button flagged as processing
   * with nothing logged anywhere.
   */
  const SERVER_SIDE_EXPORT_ZIP_TIMEOUT_MS = 60000;

  /*
   * Row ceiling for one export. `length=-1` makes DataTables render every row into the DOM before
   * draw.dt fires, so a big enough list freezes the tab outright — no toast, no timeout, just a
   * hung browser. Checked BEFORE the fetch, against the count the current draw already reported,
   * so an over-large list costs nothing and the user gets an instruction ("filter, then export")
   * instead of a hang. Generous on purpose: it's a guard against pathology, not a quota.
   */
  const SERVER_SIDE_EXPORT_MAX_ROWS = 25000;

  /*
   * portalHelper is resolved lazily so this shared service doesn't take a hard dependency on it.
   * That does NOT make the call safe: showToast reaches the bare `toastr` global, and
   * portal/document/index.html loads portal_helper.js without toastr, so the lookup succeeds and
   * the call then throws. Swallowed and reported rather than propagated, because callers release
   * the button on the line *after* the toast — a throw here would strand `exporting` at true and
   * leave the button dead for the life of the page.
   */
  function show_export_toast(type, message) {
    try {
      $injector.get('portalHelper').showToast(type, message);
    } catch (error) {
      dtuCaptureExportException(error, { tags: { report_toast: message } });
    }
  }

  /**
   * Excel export button for a `serverSide` DataTable.
   *
   * DataTables only holds the current page's rows for a server-side table, and the HTML5
   * exporters render from that row cache, so the plain excel button would silently export a
   * single page. This wrapper redraws with `length=-1` first — djangorestframework-datatables'
   * pagination short-circuits that value and returns the whole filtered queryset unpaginated —
   * exports, then puts the original paging back.
   *
   * The fetch bypasses pagination entirely, so it is only appropriate for tables that serialise
   * and render in one go; anything over SERVER_SIDE_EXPORT_MAX_ROWS is refused up front.
   *
   * The returned config carries per-export state, so it belongs to ONE table — don't hand the
   * same object to two DataTables.
   *
   * The tail arguments of get_excel_button() are grouped into `options` rather than passed
   * positionally, to stay inside the project's parameter-count limit.
   *
   * @param {string} report_name - Report name, used for the filename and in user-facing errors.
   * @param {Object} user - Current user, stamped into the sheet header by get_excel_button().
   * @param {Object} scope - Angular scope holding the table and column config.
   * @param {Object} [options] - Passed through to get_excel_button().
   * @param {boolean} [options.dropdown] - Whether the button renders inside a dropdown.
   * @param {Array} [options.custom_headers] - Optional extra header rows.
   * @param {string} [options.report_table_key=report_table] - Key on `scope` holding the DataTable.
   * @param {string} [options.report_columns_key=report_columns] - Key on `scope` holding the columns.
   * @param {Object} [options.custom_styles] - Optional styling passed through to get_excel_button().
   * @returns {Object} DataTables button config.
   */
  dataTablesUtils.get_server_side_excel_button = function (report_name, user, scope, options) {

    const opts = options || {};
    // Match the defaults the sibling helpers (get_export_buttons, get_visibility_excel_buttons)
    // apply, so this is a true drop-in for them.
    const report_table_key = opts.report_table_key || 'report_table';
    const report_columns_key = opts.report_columns_key || 'report_columns';

    const btn_excel = dataTablesUtils.get_excel_button(report_name,
      user,
      scope,
      opts.dropdown,
      opts.custom_headers,
      report_table_key,
      report_columns_key,
      opts.custom_styles
    );
    const export_action = $.fn.dataTable.ext.buttons.excelHtml5.action;
    let exporting = false;

    function too_many_rows_message(row_count) {
      return 'The ' + report_name + ' list has ' + $filter('number')(row_count) + ' rows, which is '
        + 'too many to export at once (limit ' + $filter('number')(SERVER_SIDE_EXPORT_MAX_ROWS)
        + '). Please filter or search the list, then export again.';
    }

    /*
     * The pre-fetch variant, hedged on purpose. It is judged on the last COMPLETED draw's count,
     * and server-side search is debounced by 400ms — so someone who has just narrowed a search
     * from 40,000 rows to 200 and clicked promptly would otherwise be told, as a statement of
     * fact, to go and do the thing they had just done. Say what the list last reported and what
     * to do about it, rather than asserting a count that may already be stale.
     */
    function probably_too_many_rows_message(row_count) {
      return 'The ' + report_name + ' list last reported ' + $filter('number')(row_count)
        + ' rows, which is too many to export at once (limit '
        + $filter('number')(SERVER_SIDE_EXPORT_MAX_ROWS) + '). If you have just changed the search '
        + 'or filters, wait for the list to finish updating and try again; otherwise narrow the '
        + 'list further, then export.';
    }

    btn_excel.action = function (e, dt, button, config, done) {
      let released = false;
      // Whether THIS click owns the in-flight export. `exporting` is shared across clicks, so a
      // rejected second click must not clear it — that would re-open the guard and let a third
      // click start a concurrent full-table export.
      let owns_export = false;
      /*
       * Deliberately NOT the same variable as the fetch watchdog below. This one is armed once the
       * rows are in and has to survive the synchronous teardown that follows immediately after,
       * so it must be outside everything the disarm_* helpers clear. Only release() and the
       * destroy hooks cancel it, which is exactly when the export is genuinely over.
       */
      let zip_watchdog = null;
      // Declared up here, ahead of release(), because release() has to be able to drop the destroy
      // hooks. Only populated once the export is actually armed, further down.
      const handlers = {};
      let watchdog = null;
      let unwatch_scope = null;
      /*
       * Which draw this export owns, and the only reliable way to tell our fetch's response from
       * anyone else's. _fnAjaxUpdate increments settings.iDraw and echoes it as the `draw` request
       * parameter; DatatablesRenderer returns it verbatim on both the paginated and the length=-1
       * path; _fnAjaxUpdateDraw then assigns settings.iDraw = json.draw before drawing. So the one
       * number identifies our request in handlers.xhr (read off the payload) and our draw in
       * handlers.draw (read off settings). Assigned below, once the fetch has been issued.
       */
      let our_draw = null;
      /*
       * Set to our_draw ONLY once handlers.xhr has positively validated our own payload. Negative
       * attribution is not enough on its own: DataTables' stale-draw guard is
       * `if (draw !== undefined) { if (draw < iDraw) return; iDraw = draw }`, so a 200 whose body
       * carries no `draw` at all leaves iDraw untouched — still equal to our_draw — and goes on to
       * draw. handlers.xhr would have returned early on `Number(undefined) !== our_draw`, skipping
       * the row ceiling and the completeness check, and handlers.draw would then read an unchanged
       * iDraw, judge the response ours, and export it unvalidated. That is the silent partial
       * export the whole helper exists to prevent, so the draw must be positively vouched for
       * rather than merely not-disowned. Reachable on a 204 (DataTables rewrites a null body to
       * {} before dispatching xhr.dt), a JSON auth interstitial, or any future caller pointing
       * this helper at an endpoint the DataTables renderer does not serve.
       */
      let verified_draw = null;

      function current_draw_id() {
        const settings = dt.settings()[0];
        return settings?.iDraw ?? null;
      }

      function disarm_destroy_hooks() {
        dt.off('destroy', handlers.destroy);
        if (unwatch_scope) {
          unwatch_scope();
          unwatch_scope = null;
        }
      }

      /*
       * `done` is Buttons' processing(node, false). Several paths can reach it — the exporter
       * itself, a build error, an ajax failure, either watchdog — so make it one-shot, and only
       * drop the re-entrancy flag when the export is genuinely over (the zip phase included).
       *
       * The destroy hooks are released HERE rather than in the draw handler's teardown: they are
       * the only thing that can cancel the zip watchdog if the user navigates away while the file
       * is still being written, so they have to outlive the rest of the cleanup.
       */
      function release() {
        if (released) {
          return;
        }
        released = true;
        clearTimeout(zip_watchdog);
        if (owns_export) {
          exporting = false;
        }
        /*
         * Freeing the button goes FIRST and the two steps are isolated from each other, because
         * this is the last chance to clear Buttons' processing state and each step can throw on a
         * table that is being torn down. Unhooking first — as this used to — meant a dt.off()
         * against a dead table took the release with it and left the button dead for the life of
         * the page, which is the outcome this whole helper is written to avoid.
         */
        try {
          done();
        } catch (error) {
          /*
           * `done` is Buttons' processing(node, false), and on the destroy path Buttons' own
           * teardown has already run — it registers its 'destroy' handler at table-init time, so it
           * goes first and splices our button out of its list, after which processing() looks the
           * node up, gets undefined and throws. DataTables fires destroy callbacks without a catch,
           * so letting that escape would abort the destroy and leave a `bDestroy: true` re-init
           * with no replacement table at all — a blank grid until the page is reloaded. Nothing
           * useful remains to do with the button by then, so swallow and report.
           */
          dtuCaptureExportException(error, { tags: { report: report_name } });
        }
        if (owns_export) {
          try {
            disarm_destroy_hooks();
          } catch (error) {
            dtuCaptureExportException(error, { tags: { report: report_name } });
          }
        }
      }

      // Arrow function so it inherits `this` (the Buttons context the exporter needs) lexically,
      // rather than stashing it in a variable.
      const run_export = () => {
        // The exporter builds the whole sheet and runs `customize` synchronously, deferring only
        // the zipping, so paging is safe to restore the moment this returns. Release is not:
        // that rides on the exporter's own call to the callback we hand it.
        export_action.call(this, e, dt, button, config, release);
      };

      /*
       * Buttons never disables a button while an async action is pending — its click handler
       * checks only the `disabled` class, and `async` merely defers the action — so a second
       * click reaches here while the full-table fetch is still in flight. Left alone it would arm
       * a second full-table fetch over the first, and whichever response landed second would find
       * the handlers already torn down.
       */
      if (exporting) {
        show_export_toast(CONSTANTS.typeOfToasts.INFO, 'The ' + report_name + ' export is already running.');
        release();
        return;
      }

      /*
       * get_excel_button() resolves both by key from inside exportOptions/customize, so a missing
       * one fails deep in the exporter with nothing actionable for the user. Columns are checked
       * for CONTENT, not just presence: controllers initialise them to [] and populate them once
       * the table is built, and `![]` is false — a truthiness check would wave the empty array
       * straight through into the failure this guard exists to pre-empt.
       */
      const report_columns = scope[report_columns_key];
      if (!scope[report_table_key] || !Array.isArray(report_columns) || report_columns.length === 0) {
        show_export_toast(CONSTANTS.typeOfToasts.ERROR,
          'The ' + report_name + ' table is not ready yet. Please reload the page and try again.');
        release();
        return;
      }

      // Declared here rather than inline in handlers.draw: as a nested function literal it would
      // put that code five function scopes deep, past the project's nesting limit.
      function on_zip_timeout() {
        // Feedback and release BEFORE telemetry, in every handler: this runs from a setTimeout
        // with nothing above it to catch, so anything that throws here strands the button.
        show_export_toast(CONSTANTS.typeOfToasts.ERROR,
          'The ' + report_name + ' spreadsheet could not be saved. Please try again.');
        release();
        dtuCaptureExportMessage('The ' + report_name + ' export never finished writing the file');
      }

      function guarded_export() {
        try {
          run_export();
          /*
           * Arm the zip watchdog HERE, not before the export call. excelHtml5 builds the entire
           * sheet synchronously — a DOM node per cell, per column, per row — and only then hands
           * off to generateAsync, so a timer armed beforehand is charged for the build too. JS is
           * single-threaded, so it cannot fire during that build; it would simply be overdue the
           * moment control returns, and a 25,000-row export (which the row ceiling expressly
           * allows) would tell the user the file could not be saved and log a failure to Sentry
           * seconds before the file downloaded perfectly. Worse than the false toast: it would fill
           * Sentry with that message for exports that worked, making the real generateAsync
           * rejections this watchdog exists to catch impossible to find.
           *
           * The `released` check covers JSZip's synchronous fallback, which calls back before we
           * get here — arming then would leave a timer with nothing left to cancel it.
           */
          if (!released) {
            zip_watchdog = setTimeout(on_zip_timeout, SERVER_SIDE_EXPORT_ZIP_TIMEOUT_MS);
          }
        } catch (error) {
          // Without this the button stays flagged as processing (and anything listening for
          // buttons-processing.dt never sees the release), because the callback we handed the
          // exporter is only reached from inside it.
          show_export_toast(CONSTANTS.typeOfToasts.ERROR,
            'Could not build the ' + report_name + ' spreadsheet. Please try again.');
          release();
          dtuCaptureExportException(error, { tags: { report: report_name } });
        }
      }

      /*
       * No shortcut for a table already showing every row. `dt.page.len()` reads _iDisplayLength,
       * which _fnLengthChange writes synchronously with no draw, so it reports the length
       * REQUESTED and never the length DELIVERED — exporting straight off it would write a file
       * from whatever the row cache last held, skipping the row ceiling, the completeness check,
       * the zip watchdog and the re-entrancy guard alike. A table already at -1 still issues a
       * request and completes a draw under `dt.page.len(-1).draw(false)` below, so the fully
       * guarded path handles that case for the price of one redundant round trip.
       */

      /*
       * Cheap first pass only. recordsDisplay is _iRecordsDisplay, written on the last COMPLETED
       * draw, and server-side tables get a 400ms search debounce by default — so right after the
       * user broadens a search this still reports the old, smaller count. handlers.xhr re-checks
       * against the authoritative recordsFiltered once the response arrives; that is the gate
       * that actually holds, and the one whose message states the count as fact.
       */
      /*
       * page.info() is `if (0 !== this.context.length) return {...}`, so it yields undefined for a
       * table whose context has been emptied — the same hazard lastValidPage() is guarded against
       * further up this file. Reading .recordsDisplay straight off it throws out of the button
       * action, which is upstream of `done()`: no toast, no telemetry, and the button flagged as
       * processing for the life of the page. 0 skips the cheap pre-check; handlers.xhr still
       * applies the authoritative ceiling once the response is in hand.
       */
      const page_info = dt.page.info();
      const filtered_rows = page_info?.recordsDisplay ?? 0;
      if (filtered_rows > SERVER_SIDE_EXPORT_MAX_ROWS) {
        show_export_toast(CONSTANTS.typeOfToasts.ERROR, probably_too_many_rows_message(filtered_rows));
        release();
        return;
      }

      exporting = true;
      owns_export = true;

      const original_page = dt.page();
      const original_page_len = dt.page.len();

      function restore_paging() {
        // Setting a length of -1 also forces the display start to 0, so the page index the user
        // was on has to be put back explicitly, not just the length.
        dt.page.len(original_page_len).page(original_page).draw(false);
      }

      // Split from disarm_draw_handlers(): the stateSaveParams clamp has to outlive the restore
      // draw, so a throw in restore_paging() can't leave `length: -1` persisted in the saved state.
      function disarm_fetch_handlers() {
        clearTimeout(watchdog);
        dt.off('draw.dt', handlers.draw);
        dt.off('xhr.dt', handlers.xhr);
      }

      // Everything the fetch/draw cycle needs — but NOT the destroy hooks, which release() owns.
      function disarm_draw_handlers() {
        disarm_fetch_handlers();
        dt.off('stateSaveParams.dt', handlers.state);
      }

      function abandon(message) {
        // Feedback and release first, cleanup last: restore_paging() draws, and if it throws on
        // a torn-down table the user would otherwise never be told anything at all. That applies
        // to the dt.off() calls too — this runs from a bare setTimeout on the watchdog path, so a
        // throw from them would take the toast and the release down with it.
        show_export_toast(CONSTANTS.typeOfToasts.ERROR, message);
        release();
        // Before the restore draw, which would otherwise re-enter these same handlers.
        disarm_fetch_handlers();
        try {
          /*
           * No attempt to clear DataTables' own processing state here: dt.processing(false) is a
           * no-op while settings.bDrawing is true, which it is on every path that reaches this
           * function. So when the transport wedges (see SERVER_SIDE_EXPORT_TIMEOUT_MS) the loading
           * overlay survives — which is why that message tells the user to reload rather than just
           * retry. Clearing it would mean writing to the private bDrawing flag.
           */
          restore_paging();
        } catch (error) {
          show_export_toast(CONSTANTS.typeOfToasts.ERROR,
            'The ' + report_name + ' table could not be restored. Please reload the page.');
          dtuCaptureExportException(error, { tags: { report: report_name } });
        } finally {
          // Unconditionally, for the same reason as handlers.draw: a failed restore must not leave
          // the stateSaveParams clamp bound on a table nothing will ever unhook it from.
          disarm_draw_handlers();
        }
      }

      handlers.draw = function () {
        /*
         * Only export the draw we asked for. DataTables doesn't abort an in-flight server-side
         * request, so if the user searches, sorts or repages while the full fetch is running, that
         * request supersedes ours and ITS response completes this draw — exporting a single page
         * as though it were the whole list, which is the exact bug this wrapper exists to stop.
         * Judged on the draw counter rather than on page.len(), which cannot tell our unpaginated
         * fetch from any other unpaginated fetch: a search keystroke mid-export reuses the length
         * we set, so its response would pass a page.len() check and be exported in our place.
         *
         * verified_draw is the other half and is not redundant: it requires handlers.xhr to have
         * actually vouched for this payload, which a response carrying no `draw` never gets — see
         * its declaration. Together they mean "our request, and checked".
         */
        if (current_draw_id() !== our_draw || verified_draw !== our_draw) {
          abandon('The ' + report_name + ' list changed while the export was preparing. '
            + 'Please try Export Excel again.');
          return;
        }

        disarm_fetch_handlers();

        // guarded_export() arms the zip watchdog itself, once the sheet is built and the zipping is
        // the only thing still outstanding. It is kept out of the fetch watchdog's variable so the
        // teardown below cannot cancel it.
        try {
          guarded_export();
        } finally {
          try {
            restore_paging();
          } catch (error) {
            // Same failure abandon() reports, so give it the same voice — the grid is left showing
            // every row and only a reload will fix it, which the user has no way to guess.
            show_export_toast(CONSTANTS.typeOfToasts.ERROR,
              'The ' + report_name + ' table could not be restored. Please reload the page.');
            dtuCaptureExportException(error, { tags: { report: report_name } });
          } finally {
            /*
             * Unconditionally, and in a finally of its own: a throw in restore_paging() must not
             * leave the stateSaveParams clamp bound. release() has already dropped the destroy
             * hooks by then, so nothing else would ever remove it and every future state save on
             * this table would be silently rewritten to the export-time page and length.
             *
             * Safe to drop the clamp even on that failure: page.len() applies _iDisplayLength
             * synchronously and only the draw after it can throw, so the length is already back to
             * the user's by the time we get here — there is no -1 left for a later save to persist.
             *
             * Draw handlers only. The destroy hooks stay armed so they can still cancel the zip
             * watchdog if the user leaves while the file is being written; release() drops them.
             */
            disarm_draw_handlers();
          }
        }
      };

      /*
       * The only place the payload is visible, so the only place that can tell a complete response
       * from a truncated one, or apply the row ceiling to an authoritative count.
       *
       * Returning true stops DataTables raising its own native alert() on top of the toast — but
       * ONLY on the ajax-failure path, where the error callback gates on the event result. A 200
       * carrying json.error is logged from the success handler (`(t=e.error||e.sError)&&z(n,0,t)`)
       * before xhr.dt is even dispatched, and that return value is never consulted, so the native
       * alert appears there regardless.
       */
      // DataTables dispatches this as [settings, json, settings.jqXHR]. The jqXHR is best-effort
      // only — settings.jqXHR holds whichever request started most recently, so on a failure it may
      // not be the one that just died; it is used for a status code in telemetry, nothing more.
      handlers.xhr = function (event, settings, json, xhr) {
        if (!json) {
          /*
           * An ajax failure fires xhr.dt with a null json and never reaches draw.dt. There is
           * nothing to attribute it by — `draw` only comes back on a payload — so a failure while
           * the export is armed is treated as ours and fails closed. Abandoning a healthy export
           * because an unrelated request died is the safe direction; the alternative is a user
           * staring at a spinner until the two-minute watchdog gives up.
           *
           * Reported, because `return true` below suppresses DataTables' own _fnLog for this
           * response and this table's ajax is raw DataTables config with no $http interceptor
           * behind it — so without this line a mid-export session expiry or 5xx leaves no record
           * anywhere, and the only message the user sees is export-flavoured.
           */
          abandon('Could not load the full ' + report_name + ' list to export. Please try again.');
          dtuCaptureExportMessage('The ' + report_name + ' export fetch failed',
            { extra: { status: xhr?.status } });
          return true;
        }

        /*
         * xhr.dt is dispatched for EVERY response on this table, and it fires BEFORE DataTables'
         * own stale-draw guard — so responses to requests the USER triggered land here too. Judging
         * one of those is wrong in both directions: a page-length change legitimately returns one
         * page of many and would be misreported as truncated (reverting paging the user had just
         * chosen), while a search keystroke mid-export reuses our length=-1 and would sail through
         * a page.len() check to be exported in place of the list they actually asked for. Only our
         * own draw can be judged; for anything else handlers.draw reports the interruption.
         */
        if (Number(json.draw) !== our_draw) {
          return;
        }

        if (json.error || json.sError) {
          /*
           * The detail goes to Sentry, never into the toast: the vendored toastr 2.1.4 ships
           * escapeHtml: false and app_common.js does not override it, so a server-controlled
           * string would be inserted as markup — and a structured error would read as
           * [object Object] to the user anyway.
           */
          abandon('The server could not return the full ' + report_name + ' list to export. '
            + 'Please try again.');
          dtuCaptureExportMessage('The ' + report_name + ' export was refused by the server',
            { extra: { server_error: json.error || json.sError } });
          return true;
        }

        const reported = Number.parseInt(json.recordsFiltered, 10);
        const delivered = Array.isArray(json.data) ? json.data.length : 0;
        /*
         * The real row ceiling. The pre-fetch check reads the last completed draw's count, which is
         * stale while a search is still settling; this is the first authoritative figure.
         *
         * Judged on whichever number is LARGER, because the thing that freezes the tab is the row
         * count DataTables is about to render, not the count the server claims. On the length=-1
         * path those are produced independently — paginate_queryset returns before
         * get_count_and_total_count, so recordsFiltered comes from the filter backend's count while
         * data comes from the serialized queryset — and that seam has regressed here once already
         * (RPR-6663). An understated count with an oversized payload would otherwise satisfy both
         * this check and the completeness check below and be rendered in full.
         *
         * Rows are already transferred by now and DataTables renders them after this handler
         * returns, so abandoning bounds the damage rather than preventing it outright — though in
         * practice restore_paging() bumps iDraw synchronously inside abandon(), which makes
         * DataTables drop this response at its own stale-draw guard before rendering anything.
         */
        const ceiling_count = Math.max(Number.isFinite(reported) ? reported : 0, delivered);
        if (ceiling_count > SERVER_SIDE_EXPORT_MAX_ROWS) {
          abandon(too_many_rows_message(ceiling_count));
          dtuCaptureExportMessage('The ' + report_name + ' export exceeded the row ceiling',
            { extra: { reported: json.recordsFiltered, delivered: delivered } });
          return true;
        }
        /*
         * Fails CLOSED on a missing or unparseable recordsFiltered. This is the last line of
         * defence against the silent partial export the whole feature exists to prevent, and an
         * unverifiable response is not a verified one: without the count there is nothing to
         * compare json.data against, so a header-only file would otherwise pass as complete.
         * RPRDatatablesPageNumberPagination keeps the counts intact today — this holds the line if
         * a serializer change or an intermediary ever stops it.
         *
         * Reported to Sentry as well as to the user, because tripping this means the server stopped
         * returning a verifiable full list — the export is broken for EVERYONE, not for this one
         * click, and a toast the clicking user dismisses is not a signal anybody sees. A tripwire
         * nobody is watching is not a tripwire.
         */
        if (!Array.isArray(json.data) || !Number.isFinite(reported) || delivered < reported) {
          abandon('Only part of the ' + report_name + ' list came back (' + delivered + ' of '
            + (Number.isFinite(reported) ? reported : 'an unreported number of') + ' rows), so the '
            + 'export was cancelled rather than saving an incomplete file. Please try again.');
          dtuCaptureExportMessage('The ' + report_name + ' export got an incomplete or unverifiable list',
            { extra: { reported: json.recordsFiltered, delivered: delivered } });
          return true;
        }

        // Everything checked out, so vouch for this draw. handlers.draw exports only a draw that
        // has been through the gates above; see the verified_draw declaration for why.
        verified_draw = our_draw;
      };

      // stateSave writes on every completed draw, this temporary one included, and it runs as a
      // draw callback so it lands BEFORE draw.dt fires. Keep the user's real paging in the saved
      // state so navigating away mid-export doesn't leave the screen stuck showing every row.
      handlers.state = function (event, settings, data) {
        data.length = original_page_len;
        data.start = original_page * original_page_len;
      };

      /*
       * Without these a watchdog outlives the screen: after the user navigates away it would fire
       * restore_paging() at a dead table and pop an error over an unrelated page. Covers both
       * timers — release() cancels the zip watchdog and unhooks this pair.
       *
       * Two teardown reasons, and they need OPPOSITE treatment of the stateSaveParams clamp, which
       * is why they are not the same handler.
       */

      /*
       * DataTables' own destroy(), i.e. an explicit .destroy() or a `bDestroy: true` re-init.
       * Drop the clamp here. It buys nothing — destroy() sets bDestroying and never clears it, and
       * _fnSaveState is gated on !bDestroying, so a late length=-1 response landing on these
       * settings cannot persist state either way. And it actively harms: destroy() unbinds only
       * .DT-namespaced handlers and re-inserts the SAME table node, so a handler left on .dt
       * survives into the replacement table and would rewrite every one of its state saves to this
       * export's page and length — the user's later paging silently never persisting.
       */
      handlers.destroy = function () {
        disarm_draw_handlers();
        release();
      };

      /*
       * An Angular route change, which does NOT destroy the DataTable: destroy() only runs on an
       * explicit call or a `bDestroy: true` re-init, and 2.3.8 registers no element-removal hook of
       * its own, so bDestroying stays false and the in-flight length=-1 response can
       * still land and complete a draw after this runs. _fnSaveState is an unconditional draw
       * callback gated on bStateSave && !bDestroying, so the clamp has to stay bound or that draw
       * writes {length: -1, start: 0} into the saved state and the next visit renders every row on
       * load — the tab freeze SERVER_SIDE_EXPORT_MAX_ROWS exists to prevent, reached on a path
       * where nothing re-checks the ceiling. The table is going away, so a clamp left on it has
       * nothing else to affect.
       */
      function on_scope_destroyed() {
        disarm_fetch_handlers();
        release();
      }

      dt.on('destroy', handlers.destroy);
      if (scope && angular.isFunction(scope.$on)) {
        unwatch_scope = scope.$on('$destroy', on_scope_destroyed);
      }

      watchdog = setTimeout(function () {
        // Deliberately not "please try again" on its own: the most likely way to land here leaves
        // the request itself wedged, so retrying without a reload usually fails the same way.
        // Feedback first, telemetry last — this is a timer callback with nothing above it to catch.
        abandon('The ' + report_name + ' export timed out. Please reload the page and try again.');
        dtuCaptureExportMessage('The ' + report_name + ' export never received a response');
      }, SERVER_SIDE_EXPORT_TIMEOUT_MS);

      dt.on('draw.dt', handlers.draw);
      dt.on('xhr.dt', handlers.xhr);
      dt.on('stateSaveParams.dt', handlers.state);
      dt.page.len(-1).draw(false);
      /*
       * After the draw, not before: _fnAjaxUpdate increments iDraw as part of issuing the request,
       * so this reads our request's number. The response cannot have arrived yet — it is an async
       * round trip — so both handlers are guaranteed to see this assigned. (Server-side tables
       * only, as documented: a client-side table draws synchronously and would reach handlers.draw
       * before this line, where it would be judged, correctly for this helper, as not ours.)
       */
      our_draw = current_draw_id();
    };

    return btn_excel;
  };

  /**
   * Toggles the visibility of columns in a DataTable.
   * @param {*} dt - The DataTable instance.
   * @param {*} visible - Boolean indicating whether to show or hide columns.
   */
  function toggleColumnsVisibility(dt, visible) {
    try {
      dt.columns(':not(.skipCol)').visible(visible);
    } catch (err) {
      // fallback: iterate columns
      dt.columns().every(function () {
        const header = $(this.header());
        if (header && header.hasClass && !header.hasClass('skipCol')) {
          this.visible(visible);
        }
      });
    }
  }

  // RPR-6600: Helpers to manage table UI when toggling column visibility.
  //
  // When all columns are deselected via "Deselect All", the search bar, pagination,
  // and info text should be hidden to avoid a broken layout (page navigation shifting
  // to the top and search bar shifting to the right).
  // A min-height is set on the scroll body as a temporary fix to prevent the "Select
  // Columns" dropdown from being clipped when all columns are deselected.
  const DESELECTED_SCROLL_BODY_MIN_HEIGHT = '500px';

  /** Show search bar, info text, and pagination; recalculate column widths. */
  dataTablesUtils.showTableElements = function (dt) {
    const tableWrapper = $(dt.table().container());
    tableWrapper.find('.dt-search, .dt-info, .dt-paging').show();
    tableWrapper.find('.dt-scroll-body').css('min-height', '');
    dt.columns.adjust().draw();
  };

  /** Hide search bar, info text, and pagination (used when all columns are deselected). */
  dataTablesUtils.hideTableElements = function (dt) {
    const tableWrapper = $(dt.table().container());
    tableWrapper.find('.dt-search, .dt-info, .dt-paging').hide();
    tableWrapper.find('.dt-scroll-body').css('min-height', DESELECTED_SCROLL_BODY_MIN_HEIGHT);
  };

  /**
   * True while at least one user-toggleable column is visible.
   * @param {*} dt - The DataTable instance.
   * @returns {boolean}
   */
  dataTablesUtils.hasVisibleUserColumns = function (dt) {
    return dt.columns(':not(.skipCol)').visible().toArray().includes(true);
  };

  /**
   * Sets up column visibility event listener to handle table width issues
   * Call this function after initializing DataTable with visibility buttons
   */
  dataTablesUtils.setupColumnVisibilityListener = function (dt) {
    // Add event listener for column visibility changes if not already added
    if (!dt._dataTablesUtils_hasColvisListener) {
      dt.on('column-visibility.dt', function (e, settings, column, state) {
        // RPR-6600: Show or hide search, info, and pagination based on column visibility.
        if (dataTablesUtils.hasVisibleUserColumns(dt)) {
          dataTablesUtils.showTableElements(dt);
        } else {
          dataTablesUtils.hideTableElements(dt);
        }
      });
      dt._dataTablesUtils_hasColvisListener = true;
    }
  };

  /**
   * Hides the search/info/paging controls when a table is built with every column already
   * hidden. Init-only: state restored at init sets column visibility directly, without firing
   * column-visibility, so setupColumnVisibilityListener never sees it. Never re-shows the
   * controls — a freshly built wrapper has them visible, and the listener handles every later
   * change.
   * @param {*} dt - The DataTable instance.
   */
  dataTablesUtils.reconcileColumnVisibilityElements = function (dt) {
    if (!dataTablesUtils.hasVisibleUserColumns(dt)) {
      dataTablesUtils.hideTableElements(dt);
    }
  };

  /**
   * Removes column visibility event listener and cleans up stored data
   * Call this function when destroying the DataTable to prevent memory leaks
   */
  dataTablesUtils.cleanupColumnVisibilityListener = function (dt) {
    if (dt._dataTablesUtils_hasColvisListener) {
      dt.off('column-visibility.dt');
      dt._dataTablesUtils_hasColvisListener = false;

      // Restore table elements visibility on cleanup.
      // Avoid calling showTableElements() here as it triggers dt.columns.adjust().draw()
      // which is unsafe during destroy — DataTables internal state may be partially torn down.
      const tableWrapper = $(dt.table().container());
      tableWrapper.find('.dt-search, .dt-info, .dt-paging').show();
      tableWrapper.find('.dt-scroll-body').css('min-height', '');
    }
  };

  dataTablesUtils.get_colvis_button = function () {
    return {
      extend: 'colvis',
      collectionLayout: 'two-column',
      columns: ':not(.skipCol)',
      text: 'Toggle Columns',
      className: 'mat ui toggle-column-button small primary button',
      columnText: function (dt, idx, title) {
        return title.length > 20 ? title.substring(0, 18) + '&hellip;' : title;
      }
    };
  };

  dataTablesUtils.get_visibility_button = function () {
    return {
      extend: 'collection',
      text: '<i class="fa fa-th me-2"></i>Toggle Columns',
      className: 'mat ui toggle-column-button small primary button',
      autoClose: true,
      init: function (dt, node, config) {
        // Set up column visibility listener when button is initialized
        dataTablesUtils.setupColumnVisibilityListener(dt);

        // Most tables reaching this helper already pass stateSave: true (default stateDuration,
        // so localStorage), and restored visibility bypasses the listener above — reconcile here
        // too, not only in get_session_state_options(). The wrapper doesn't exist yet during
        // button init, hence the deferral; reconciling is hide-only, so running twice is a no-op.
        dt.one('init.dt', function () {
          dataTablesUtils.reconcileColumnVisibilityElements(dt);
        });

        // Set up cleanup when DataTable is destroyed
        dt.on('destroy.dt', function() {
          dataTablesUtils.cleanupColumnVisibilityListener(dt);
        });

        // Remove the dropdown triangle indicator
        $(node).find('.dt-button-down-arrow').remove();
        $(node).find('span.dt-down-arrow').remove();
      },
      buttons: [
        {
          text: '<i class="fa fa-check-square-o me-2"></i>Select All',
          className: 'mat ui small primary button',
          action: function (e, dt, node, config) {
            toggleColumnsVisibility(dt, true);
            // RPR-6600: Restore search, info, and pagination after re-enabling all columns
            dataTablesUtils.showTableElements(dt);
          }
        },
        {
          text: '<i class="fa fa-square-o me-2"></i> Deselect All',
          className: 'mat ui small primary button',
          action: function (e, dt, node, config) {
            toggleColumnsVisibility(dt, false);
            // RPR-6600: Hide search, info, and pagination when no columns are visible
            dataTablesUtils.hideTableElements(dt);
          }
        },
        {
          extend: 'colvis',
          collectionLayout: 'two-column',
          text: '<i class="fa fa-columns me-2"></i>Select Columns',
          className: 'mat ui small primary button',
          columns: ':not(.skipCol)',
          columnText: function (dt, idx, title) {
            return title.length > 20 ? title.substring(0, 18) + '&hellip;' : title;
          },
          init: function (dt, node, config) {
            // Remove the dropdown triangle indicator
            $(node).find('.dt-button-down-arrow').remove();
            $(node).find('span.dt-down-arrow').remove();
          }
        }
      ]
    };
  };

  /**
     * Generates the export buttons for a DataTable
     * @param {string} report_name        The name of the report.
     * @param {Object} user               The user object.
     * @param {Object} scope              The parent scope.
     * @param {boolean} dropdown          Whether to render buttons in a dropdown
     * @param {boolean} show_visibility_button  Whether to show the ColVis button, not used just to match
     * defination with get_export_buttons
     * @param {string[]} report_subtitle  Subtitle or custom headers to add to exported reports
     * @param {string} report_table_key   Key used for the Report Table in scope. (Default: 'report_table')
     * @param {string} report_columns_key Key used for Report Columns in scope. (Default: 'report_columns')
     * @param {Object} custom_styles      Custom style object for excel sheet
     * @returns {[*, *]}
     */
  dataTablesUtils.get_visibility_excel_buttons = function (report_name, user,
    scope, dropdown, show_visibility_button, custom_headers, report_table_key, report_columns_key, custom_styles) {

    /*
      note : show_visibility_button is not used and is just passed to match
      function definition of get_exports_button because in loadReport in admin_reports_controller
      we have a single call for all export button functions so their definition should match
    */
    report_table_key = (report_table_key == null || report_table_key == '')
      ? 'report_table'
      : report_table_key;

    report_columns_key = (report_columns_key == null || report_columns_key == '')
      ? 'report_columns'
      : report_columns_key;

    const btn_excel = dataTablesUtils.get_excel_button(report_name,
      user,
      scope,
      dropdown,
      custom_headers,
      report_table_key,
      report_columns_key,
      custom_styles
    );
    const btn_visibility = dataTablesUtils.get_visibility_button();

    return [btn_visibility, btn_excel];
  };


  /**
     * Generates the export buttons for a DataTable
     * @param {string} report_name        The name of the report.
     * @param {Object} user               The user object.
     * @param {Object} scope              The parent scope.
     * @param {boolean} dropdown          Whether to render buttons in a dropdown
     * @param {boolean} show_visibility_button  Whether to show the ColVis button
     * @param {string[]} custom_headers   Subtitle or custom headers to add to exported reports
     * @param {string} report_table_key   Key used for the Report Table in scope. (Default: 'report_table')
     * @param {string} report_columns_key Key used for Report Columns in scope. (Default: 'report_columns')
     * @returns {[*, *, *]}
     */
  dataTablesUtils.get_export_buttons = function (report_name, user, scope, dropdown,
    show_visibility_button, custom_headers, report_table_key, report_columns_key, custom_styles) {
    // custom_headers will be appended after report name and report date, each on separate line.

    report_table_key = (report_table_key == null || report_table_key == '')
      ? 'report_table'
      : report_table_key;

    report_columns_key = (report_columns_key == null || report_columns_key == '')
      ? 'report_columns'
      : report_columns_key;

    if (!angular.isDefined(scope[report_columns_key])) {
      alert(
        'report_columns is not defined. Please check the provided report_columns_key if applicable.');
      return;
    }

    const btn_pdf = dataTablesUtils.get_pdf_button(report_name,
      user,
      scope,
      dropdown,
      custom_headers,
      report_table_key,
      report_columns_key);
    const btn_excel = dataTablesUtils.get_excel_button(report_name,
      user,
      scope,
      dropdown,
      custom_headers,
      report_table_key,
      report_columns_key,
      custom_styles);
    const btn_csv = dataTablesUtils.get_csv_button(report_name,
      user,
      scope,
      dropdown,
      custom_headers,
      report_table_key,
      report_columns_key);
    const btn_visibility = dataTablesUtils.get_visibility_button();

    const result = [btn_pdf, btn_excel, btn_csv];
    if (!dropdown && show_visibility_button) {
      result.unshift(btn_visibility);
    }

    return result;
  };

  /**
   * Returns copies of `columns` carrying a stable `name`, derived from each column's own `data` or
   * `title`. Required by any table using get_session_state_options(): DataTables only remaps saved
   * state by name when every column has one, and falls back to restoring visibility and sort
   * POSITIONALLY otherwise — which silently hides the wrong column once the array is reordered.
   * Deriving from the column's identity rather than its index is what keeps a reorder safe.
   *
   * Names need only be unique within a table (state is stored per table id). Renaming a column's
   * title changes its derived name, which drops just that column's saved state — DataTables
   * discards names it cannot match.
   * @param {Array} columns - DataTable column definitions.
   * @returns {Array} Copies of the columns, each with a `name`.
   */
  dataTablesUtils.with_stable_names = function (columns) {
    return columns.map(function (column, index) {
      const identity = column.name || column.data || column.title;
      // split/filter rather than a replace + trim: an anchored `_+$` backtracks super-linearly.
      const words = String(identity).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      // An identity with no alphanumerics at all (a symbol-only title) would derive to '', and a
      // blank name is skipped by the remap, silently costing that column its state. Fall back to
      // the index: no worse than the positional restore, and it keeps every name non-empty.
      return {...column, name: words.join('_') || 'column_' + index};
    });
  };

  /**
   * Responsive `details` options that stop a cell click from collapsing its row's values away.
   *
   * Responsive's default toggle target is column 0, which on a bulk-select grid is the select
   * checkbox — so ticking a row hid that row's collapsed values. Both keys are needed together:
   * `td.no-such-cell` matches nothing, and `type: ''` stops Responsive replacing that target with
   * its own control cells, which is what `type: 'inline'` (the documented default) does. Returns a
   * fresh object per call because Responsive writes to `target` in place.
   *
   * Spread it into a `responsive.details` block alongside that table's own `display`/`renderer`.
   *
   * @returns {{type: string, target: string}} Responsive details options.
   */
  dataTablesUtils.no_collapse_on_cell_click = function () {
    return {type: '', target: 'td.no-such-cell'};
  };

  /**
   * DataTable options that persist column visibility and sort order for the session only.
   * stateDuration -1 routes the state to sessionStorage; authHelper.clearTablePreferences()
   * drops the DataTables_* keys when the session identity changes, so a new login starts from
   * defaults.
   *
   * Opting a table in also requires passing its columns through with_stable_names() — without a
   * name on every column DataTables restores state positionally.
   * @returns {object} Options to spread into a DataTable config.
   */
  dataTablesUtils.get_session_state_options = function () {
    return {
      stateSave: true,
      stateDuration: -1,
      stateSaveParams: function (settings, data) {
        // Persist column visibility and sort order only. Everything else in the state object
        // is dropped: the search term, the page position, the page length (which would
        // otherwise override the config's pageLength), and any per-column filter, whose
        // restore would outlive the UI control that set it.
        delete data.search;
        delete data.length;
        data.start = 0;
        data.columns.forEach(function (column) {
          delete column.search;
        });
      },
      stateLoaded: function (settings) {
        // The wrapper doesn't exist yet while state is being applied.
        $(settings.nTable).one('init.dt', function () {
          dataTablesUtils.reconcileColumnVisibilityElements(new $.fn.dataTable.Api(settings));
        });
      }
    };
  };

  this.getRowByElement = function (table_id, elt) {
    const table = $('#' + table_id).DataTable();
    return table.row($(elt).parent().parent()).data();
  };

  this.getTdByMeta = function (meta) {
    if (!meta) return;
    const api = new $.fn.dataTable.Api(meta.settings);
    return $(api.cell(meta.row, meta.col).node());
  };

  this.createRenderActiveFunction = function (toggleFnName) {
    return function (name, type, row) {
      if (type === 'filter') {
        if (name == true) {
          return ACTIVE;
        } else {
          return INACTIVE;
        }
      }

      if (name == true) {
        return '<input type="button" value="Deactivate" onclick="javascript:' + toggleFnName +
            '(this, ' + row.id + ', true)" id="ctl00_MainContent_grd_ctl00_ctl06_5" ' +
            'class="mat ui critical button" >';
      } else {
        return '<input type="button" value="Activate" onclick="javascript:' + toggleFnName +
            '(this, ' + row.id + ', false)" id="ctl00_MainContent_grd_ctl00_ctl04_ReActivate_9" ' +
            'class="mat ui primary button" /> ';
      }
    };
  };

  dataTablesUtils.get_green_icon = function (title) {
    return '<i title=\'' + title + '\' class=\'green fa fa-fw fa-check-circle\'></i>';
  };

  dataTablesUtils.get_yellow_icon = function (title) {
    return '<i title=\'' + title + '\' class=\'amber fa fa-fw fa-exclamation-circle\'></i>';
  };

  dataTablesUtils.get_red_icon = function (title) {
    return '<i title=\'' + title + '\' class=\'red fa fa-fw fa-exclamation-circle\'></i>';
  };

  dataTablesUtils.get_grey_icon = function (title) {
    return '<i title=\'' + title + '\' class=\' fa fa-fw fa-circle\'></i>';
  };

  dataTablesUtils.renderStatusSubmittedPayerResponse = function () {
    const states = [
      { 'data': -1, 'title': 'Expired Assignment', 'class': 'red fa fa-fw fa-calendar' },
      { 'data': 2, 'title': 'Pending Approval', 'class': 'red fa fa-fw fa-exclamation-circle' },
      {
        'data': 1,
        'title': 'Pending Response from Payer',
        'class': 'amber fa fa-fw fa-exclamation-circle'
      },
      { 'data': 0, 'title': 'Response Approved', 'class': 'green fa fa-fw fa-check-circle' }
    ];

    //State 1 - Pending Approval (Red exclamation circle)
    //State 2 - Pending Response from Payer (Yellow exclamation circle)
    //State 3 - Expired Assignment (Red Calendar)
    //State 4 - Response Approved (Green Checkbox circle)

    const getIcon = function (data, row) {
      if (row.expired) {
        return '<i title=\'' + states[0].title + '\' class=\'' + states[0].class + '\'></i>';
      }

      for (let i = 0; i < states.length; i++) {
        if (data == states[i].data) {
          return '<i title=\'' + states[i].title + '\' class=\'' + states[i].class + '\'></i>';
        }
      }
    };

    return function (data, type, row) {
      return '<div style="width:65px; text-align:center;">' + getIcon(data, row) +
          '<span style="display:none;">' + data + '</span>' + // for sorting to work
          '</div>';
    };
  };

  dataTablesUtils.renderPaymentStatus = function (hasPayment, row) {
    if (row?.is_third_party) {
      return '<span class="badge bg-secondary">N/A</span>';
    }
    // A missing value is unknown, not "Not Generated" — guards a caller that binds the wrong field.
    // This must live in the renderer: DataTables runs `render` before falling back to `defaultContent`,
    // and since this always returns a badge string, a column `defaultContent` here could never fire.
    if (hasPayment === undefined || hasPayment === null) {
      return '<span class="badge bg-secondary">N/A</span>';
    }
    if (hasPayment === true) {
      return '<span class="badge bg-success"><i class="fa fa-check"></i> Generated</span>';
    }
    return '<span class="badge bg-secondary"><i class="fa fa-times"></i> Not Generated</span>';
  };

  // Shared "Payment Status" DataTable column, used by the Fusion and IDI stakeholder grids.
  // `dataField` is the boolean the badge reflects (e.g. 'has_payment' or 'has_generated_payment').
  // The badge is third-party-aware (row with `is_third_party` -> "N/A") and treats a missing/undefined
  // `dataField` as "N/A" rather than a confident "Not Generated" — both handled in renderPaymentStatus.
  dataTablesUtils.paymentStatusColumn = function (dataField) {
    return {
      data: dataField,
      title: 'Payment Status',
      responsivePriority: 15,
      render: (data, _type, row) => dataTablesUtils.renderPaymentStatus(data, row)
    };
  };

  // Shared "Relevance Rating" DataTable column for the Fusion and IDI stakeholder grids.
  // `$scope` supplies the permission checks; `buildEditIcon(row)` returns the grid-specific
  // edit-rating control HTML (the two grids use different markup / controller callbacks).
  dataTablesUtils.relevanceRatingColumn = function ($scope, buildEditIcon) {
    return {
      data: 'relevance_rating',
      title: 'Relevance Rating',
      defaultContent: 'N/A',
      responsivePriority: 15,
      render: (_data, type, row) => {
        const rating = row.relevance_rating?.rating ?? null;
        if (type === 'export') {
          return rating === null ? '' : rating;
        }
        if (type === 'sort' || type === 'type' || type === 'filter') {
          return rating === null ? -1 : rating;
        }
        const ratingStars = `<star-rating-view rating="${rating}" max="payer_relevance_rating_choices.length - 1"></star-rating-view>`;
        let ret = '';
        if ($scope.has_permission($scope.PERMISSIONS.CAN_VIEW_RELEVANCE_RATING)) {
          ret += ratingStars;
        }
        if ($scope.has_permission($scope.PERMISSIONS.CAN_UPDATE_RELEVANCE_RATING)) {
          ret += buildEditIcon(row);
        }
        return ret;
      }
    };
  };

  dataTablesUtils.get_render_btn_action2 = function (wrapper_fn_name, scope_fn_name, btn_text, btn_class) {
    return function (data, type, row) {
      return '<a onclick="javascript:' + wrapper_fn_name + '(this, \'' + scope_fn_name + '\', ' + data + ')" class="' + btn_class + '" >' + btn_text + '</a>';
    };
  };

  dataTablesUtils.get_render_checkbox = function (wrapper_fn_name, scope_fn_name) {
    return function (data, type, row) {
      return '<input type="checkbox" onchange="' + wrapper_fn_name + '(this, \'' + scope_fn_name + '\', ' + data + ')" />';
    };
  };

  dataTablesUtils.render = function (toggleFnName, btnTextTrue, btnTextFalse) {
    return function (data, type, row) {
      if (type === 'filter') {
        if (data == true) {
          return ACTIVE;
        } else {
          return INACTIVE;
        }
      }

      if (data == true) {
        return '<input type="button" value="' + btnTextTrue + '" onclick="javascript:' +
            toggleFnName + '(this, ' + row.id + ', true)" id="ctl00_MainContent_grd_ctl00_ctl06_5" ' +
            'class="mat ui critical button" >';
      } else {
        return '<input type="button" value="' + btnTextFalse + '" onclick="javascript:' +
            toggleFnName + '(this, ' + row.id + ', false)" ' +
            'id="ctl00_MainContent_grd_ctl00_ctl04_ReActivate_9" class="mat ui primary button" /> ';
      }
    };
  };
}]
);
